import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError } from '../../services/api';
import {
  addOrganizationSampleData,
  getOrganizationSampleData,
  removeOrganizationSampleData,
  retrySampleCalls,
} from '../../services/sample-data';
import type {
  OrganizationSampleDataStatus,
  SampleDataSkippedItem,
} from '../../types/sampleData';

export type SampleDataLoadState =
  | { kind: 'loading' }
  /** The API answered 404: this server predates the endpoint, so the panel hides itself. */
  | { kind: 'unavailable' }
  | { kind: 'failed'; message: string }
  | { kind: 'ready'; status: OrganizationSampleDataStatus };

export type SampleDataFeedback =
  | { kind: 'success'; text: string; skipped: SampleDataSkippedItem[] }
  | { kind: 'error'; text: string }
  | null;

export type SampleDataAction = 'add' | 'remove' | 'retryCalls';

const FALLBACK_ERROR: Record<SampleDataAction, string> = {
  add: 'Could not add sample data. Please try again.',
  remove: 'Could not remove sample data. Please try again.',
  retryCalls: 'Could not add sample calls. Please try again.',
};

/**
 * What to announce after a retry the server accepted, keyed on where the calls ended up. A retry
 * that failed again is an error, so it lands in the alert banner instead of reading as a no-op.
 */
const retriedCallsFeedback = (status: OrganizationSampleDataStatus): SampleDataFeedback => {
  switch (status.callsStatus) {
    case 'Seeded':
      return { kind: 'success', text: 'Sample calls added.', skipped: [] };
    case 'Seeding':
      return { kind: 'success', text: 'Sample calls are being added.', skipped: [] };
    case 'Failed':
      return {
        kind: 'error',
        text: status.callsError
          ? `Adding sample calls failed again: ${status.callsError}`
          : 'Adding sample calls failed again.',
      };
    default:
      return null;
  }
};

/**
 * A 409 or 400 carries a message written for the admin ("Save a valid sample data template
 * first."). Anything else would surface a raw "API request failed: 500", so it gets plain copy.
 */
const messageFor = (err: unknown, fallback: string): string =>
  err instanceof ApiError && (err.statusCode === 400 || err.statusCode === 409) && err.message
    ? err.message
    : fallback;

const removedText = (removed: number): string =>
  `Removed ${removed} ${removed === 1 ? 'record' : 'records'}.`;

/**
 * Owns one organization's sample data panel: loading its status, adding a set, removing it and
 * retrying sample calls that failed.
 * Key the consumer on the organization id so a different organization starts from a clean slate.
 */
export const useOrganizationSampleData = (organizationId: string) => {
  const [load, setLoad] = useState<SampleDataLoadState>({ kind: 'loading' });
  const [reloadToken, setReloadToken] = useState(0);
  const [pending, setPending] = useState<SampleDataAction | null>(null);
  const [feedback, setFeedback] = useState<SampleDataFeedback>(null);
  // A ref as well as state: a second click can land before the disabled button re-renders.
  const pendingRef = useRef(false);
  const actionAbortRef = useRef<AbortController | null>(null);
  const loadAbortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    loadAbortRef.current = controller;
    getOrganizationSampleData(organizationId, controller.signal)
      .then((status) => setLoad({ kind: 'ready', status }))
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        if (err instanceof ApiError && err.statusCode === 404) {
          setLoad({ kind: 'unavailable' });
          return;
        }
        setLoad({ kind: 'failed', message: 'Could not load sample data for this organization.' });
      });
    return () => controller.abort();
  }, [organizationId, reloadToken]);

  useEffect(() => () => actionAbortRef.current?.abort(), []);

  /** Refetches without blanking the panel, so the counts do not flash away mid-refresh. */
  const refresh = useCallback(() => setReloadToken((token) => token + 1), []);

  const retry = useCallback(() => {
    setLoad({ kind: 'loading' });
    refresh();
  }, [refresh]);

  const run = useCallback(
    /** Resolves true when the request succeeded. */
    async (
      action: SampleDataAction,
      request: (signal: AbortSignal) => Promise<void>
    ): Promise<boolean> => {
      if (pendingRef.current) return false;
      pendingRef.current = true;
      // A refetch still in flight started before this action, so its answer is already stale and
      // would put back the status the action is about to replace.
      loadAbortRef.current?.abort();
      const controller = new AbortController();
      actionAbortRef.current = controller;
      setPending(action);
      setFeedback(null);
      try {
        await request(controller.signal);
        return true;
      } catch (err) {
        if (controller.signal.aborted) return false;
        setFeedback({ kind: 'error', text: messageFor(err, FALLBACK_ERROR[action]) });
        /*
          For add and remove a 409 means the panel's picture is out of date, so bring it back in
          line. A retry's 409 (no user yet) is not stale state; its 400 (no set, or calls already
          added) is.
        */
        const staleStatusCode = action === 'retryCalls' ? 400 : 409;
        if (err instanceof ApiError && err.statusCode === staleStatusCode) refresh();
        return false;
      } finally {
        pendingRef.current = false;
        if (!controller.signal.aborted) setPending(null);
      }
    },
    [refresh]
  );

  const add = useCallback(
    () =>
      run('add', async (signal) => {
        const status = await addOrganizationSampleData(organizationId, signal);
        setLoad({ kind: 'ready', status });
        setFeedback({ kind: 'success', text: 'Sample data added.', skipped: [] });
      }),
    [organizationId, run]
  );

  const remove = useCallback(
    () =>
      run('remove', async (signal) => {
        const result = await removeOrganizationSampleData(organizationId, signal);
        if (result.status) setLoad({ kind: 'ready', status: result.status });
        else refresh();
        setFeedback({ kind: 'success', text: removedText(result.removed), skipped: result.skipped });
      }),
    [organizationId, refresh, run]
  );

  const retryCalls = useCallback(
    () =>
      run('retryCalls', async (signal) => {
        const status = await retrySampleCalls(organizationId, signal);
        setLoad({ kind: 'ready', status });
        setFeedback(retriedCallsFeedback(status));
      }),
    [organizationId, run]
  );

  return { load, pending, feedback, retry, add, remove, retryCalls };
};
